import { useEffect, useState } from "react";
import useReveal from '../../hooks/useReveal';
import { useParams, useNavigate } from "react-router-dom";
import { getBlogBySlug, getBlogs, BLOG_API_BASE_URL } from "../../hooks/remote/blogApi";
import blog1 from '../../assets/images/blog1.png';
import blog2 from '../../assets/images/blog2.png';
import blog3 from '../../assets/images/blog3.png';
import blog4 from '../../assets/images/blog4.png';
import blog5 from '../../assets/images/blog5.png';
import SEO from "../../components/SEO";
import Footer from "../../components/footer";
import { extractErrorMessage } from "../../hooks/constants";
import { toast } from "react-toastify";

const SingleBlogPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [similarPosts, setSimilarPosts] = useState([]);
    const [copied, setCopied] = useState(false);
    const [articleRef, articleVisible] = useReveal();
    const [shareRef, shareVisible] = useReveal();

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                setError("");
                const data = await getBlogBySlug(slug);
                if (!data || !data.id) {
                    setBlog(null);
                    return;
                }

                // Normalize images to an array
                const normalizedImages = Array.isArray(data.images)
                    ? data.images
                    : typeof data.images === "string" && data.images.trim()
                        ? [data.images]
                        : [];

                // Ensure featuredImage has leading slash for server URL joining
                const normalizedFeatured = data.featuredImage
                    ? data.featuredImage.startsWith("/")
                        ? data.featuredImage
                        : "/" + data.featuredImage
                    : null;

                setBlog({
                    ...data,
                    images: normalizedImages,
                    featuredImage: normalizedFeatured,
                });

                if (data.Category?.slug) {
                    try {
                        const all = await getBlogs({ category: data.Category.slug });
                        const others = (Array.isArray(all) ? all : []).filter(b => b.id !== data.id);
                        const shuffled = others.sort(() => Math.random() - 0.5);
                        setSimilarPosts(shuffled.slice(0, 4));
                    } catch {
                        // similar posts are non-critical, fail silently
                    }
                }
            } catch (err) {
                console.error("Fetch blog error:", err);
                const errorMsg = extractErrorMessage(err);
                setError(errorMsg || "Failed to load blog post");
                toast.error(errorMsg || "Failed to load blog post", { theme: "colored" });
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
        window.scrollTo(0, 0);
    }, [slug]);

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
        return `${BLOG_API_BASE_URL.replace("/api", "")}${cleanPath}`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-blue-50 dark:bg-[#141D26]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 dark:border-primaryBlue mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300 text-lg">Loading blog post...</p>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="min-h-screen bg-blue-50 dark:bg-[#141D26] flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8">
                        <svg
                            className="w-20 h-20 text-red-500 dark:text-red-400 mx-auto mb-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white crimson-font">
                            Blog Post Not Found
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {error || "This blog post doesn't exist or has been removed."}
                        </p>
                        <button
                            onClick={() => navigate("/blog")}
                            className="bg-blue-600 dark:bg-primaryBlue text-white px-8 py-3 rounded-lg hover:bg-blue-700 dark:hover:bg-primaryBlue/90 transition font-medium inline-flex items-center gap-2"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                />
                            </svg>
                            Back to All Blogs
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <SEO
                title={blog.metaTitle || `${blog.title} | CloudVantage Blog`}
                description={blog.metaDescription || blog.excerpt || blog.content.substring(0, 160).replace(/<[^>]*>/g, "")}
                keywords={blog.metaKeywords || `${blog.title}, ${blog.Category?.name || ""}, cloud blog, cloudvantage`}
                author={blog.Author?.name || "CloudVantage"}
                ogTitle={blog.metaTitle || blog.title}
                ogDescription={blog.metaDescription || blog.excerpt}
                ogImage={blog.ogImage || getImageUrl(blog.featuredImage)}
                ogUrl={window.location.href}
                canonicalUrl={blog.canonicalUrl || window.location.href}
                noIndex={blog.noIndex}
                publishedTime={blog.publishedAt}
                modifiedTime={blog.updatedAt}
                schema={blog.schema}
            />
            <div className="min-h-screen bg-blue-50 dark:bg-[#141D26]">
                <div className="max-w-4xl mx-auto px-4 py-10">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-8 font-medium text-md transition group"
                    >
                        <svg
                            className="w-6 h-6 mr-2 transform group-hover:-translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Back
                    </button>

                    {/* Blog Content Card */}
                    <article ref={articleRef} className={`bg-whiteoverflow-hidden ${articleVisible ? 'animate-reveal' : 'opacity-0'}`}>
                        {/* blog title */}
                        <h1 className="text-3xl md:w-2/3 font-bold mb-6 text-gray-900 dark:text-white leading-tight crimson-font">
                          {blog.title}
                        </h1>

                        {/* Featured Image */}
                        {blog.featuredImage && (
                            <div className="aspect-video rounded-md overflow-hidden">
                                <img
                                    src={getImageUrl(blog.featuredImage)}
                                    alt={blog.title}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                    }}
                                />
                            </div>
                        )}

                        {/* Blog Content */}
                        <div className="">
                            {/* Blog Header */}
                            <div className="mb-8">
                                {/* Meta Info */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-gray-600 dark:text-gray-300 text-sm pt-5">
                                    {blog.Author && (
                                        <div className="grid gap-1">
                                            <span className="opacity-50">Published by:</span>
                                            <span className="font-medium">{blog.Author.name}</span>
                                        </div>
                                    )}

                                    {blog.Category && (
                                        <div className="grid gap-1">
                                            <span className="opacity-50">Category:</span>
                                            <button
                                                onClick={() =>
                                                    navigate(`/blog?category=${blog.Category.slug}`)
                                                } className="text-left">
                                                {blog.Category.name}
                                            
                                            </button>
                                        </div>
                                    )}

                                    {blog.readTime && (
                                        <div className="grid gap-1">
                                            <span className="opacity-50">Read Time:</span>
                                            <span>{blog.readTime}</span>
                                        </div>
                                    )}

                                    {blog.publishedAt && (
                                        <div className="grid gap-1">
                                            <span className="opacity-50">Date published:</span>
                                            <span>{formatDate(blog.publishedAt)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            <hr className="border-t-2 border-gray-200 dark:border-gray-700 mb-8" />

                            {/* Blog Content - Render HTML
                            <div className="prose prose-lg max-w-none">
                                <div
                                    className="text-gray-800 dark:text-gray-200 leading-relaxed ql-editor wrap-anywhere"
                                    style={{
                                        fontSize: "1rem",
                                        lineHeight: "1.875rem",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: blog.content }}
                                />
                            </div> */}

                            {/* Blog Content - Render HTML */}
                            <div className="prose prose-lg max-w-none dark:prose-invert">
                                <div
                                    className="blog-content text-gray-800 dark:text-gray-200 leading-relaxed wrap-anywhere"
                                    dangerouslySetInnerHTML={{ __html: blog.content }}
                                />
                            </div>
                               {/* Bottom Actions */}
                            {similarPosts.length > 0 && (
                                <div className="mt-12 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white crimson-font mb-6">
                                        Similar posts in {blog.Category?.name}
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {similarPosts.map((b, index) => {
                                            const fallbackImages = [blog1, blog2, blog3, blog4, blog5];
                                            const imageUrl = getImageUrl(b.featuredImage);
                                            return (
                                                <div
                                                    key={b.id || index}
                                                    className="overflow-hidden cursor-pointer"
                                                    onClick={() => navigate(`/blog/${b.slug}`)}
                                                >
                                                    <img
                                                        src={imageUrl || fallbackImages[index % 5]}
                                                        className="aspect-video rounded-lg object-cover w-full"
                                                        alt={b.title}
                                                        onError={(e) => {
                                                            e.currentTarget.src = fallbackImages[index % 5];
                                                        }}
                                                    />
                                                    <div className="pt-4">
                                                        <div className="flex items-center gap-2 mb-2 text-sm">
                                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                                {b.Author?.name || 'Author'}
                                                            </p>
                                                            <span>•</span>
                                                            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2 rounded-md text-xs font-semibold">
                                                                {b.Category?.name || 'Uncategorized'}
                                                            </span>
                                                        </div>
                                                        <h3 className="font-bold text-lg mb-1 text-black dark:text-white crimson-font leading-tight">
                                                            {b.title}
                                                        </h3>
                                                        <div
                                                            className="blog-preview text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2"
                                                            dangerouslySetInnerHTML={{
                                                                __html: b.excerpt || b.content?.substring(0, 200) + '...',
                                                            }}
                                                        />
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/blog/${b.slug}`);
                                                            }}
                                                            className="text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline"
                                                        >
                                                            Read More
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </article>

                    {/* Share Section */}
                    <div ref={shareRef} className={`mt-8 ${shareVisible ? 'animate-reveal' : 'opacity-0'}`}>
                        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">Share this post</p>
                        <div className="flex flex-wrap gap-3">
                            {/* Copy Link */}
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.href);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium transition"
                            >
                                {copied ? (
                                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                )}
                                {copied ? 'Copied!' : 'Copy link'}
                            </button>

                            {/* WhatsApp */}
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(blog.title + ' ' + window.location.href)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-medium transition"
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp
                            </a>

                            {/* Twitter / X */}
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black hover:bg-gray-900 text-white text-sm font-medium transition"
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                X (Twitter)
                            </a>

                            {/* LinkedIn */}
                            <a
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] hover:bg-[#0958a8] text-white text-sm font-medium transition"
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            {/* <Footer /> */}
        </div>
    );
};

export default SingleBlogPage;